/**
 * importTemplates.js — modèles Excel téléchargeables pour la « Reprise » (Lot G).
 *
 * Les classeurs sont fabriqués À LA VOLÉE côté client avec la lib `xlsx` déjà
 * embarquée : aucun binaire au dépôt, aucun endpoint à maintenir, et les
 * en-têtes restent la source unique partagée avec les modales d'import
 * (mêmes libellés que ceux que le mapping tolérant reconnaît).
 *
 * Deux modèles :
 *   - downloadDriverTemplate()  → conducteurs (branché sur DriverImportModal)
 *   - downloadVehicleTemplate() → véhicules  (à brancher sur VehicleImportModal)
 *
 * Chaque classeur porte deux feuilles : « Données » (en-têtes + 2 lignes
 * d'exemple à écraser) et « Instructions » (une ligne par colonne : obligatoire
 * ou non, format attendu, valeurs acceptées).
 */
import * as XLSX from 'xlsx';

// --- Colonnes conducteurs -----------------------------------------------------
// `key` sert de repère interne ; `header` est l'en-tête écrit dans le fichier.
const DRIVER_COLUMNS = [
  { header: 'Nom', required: true, help: 'Nom de famille du conducteur.', ex1: 'Kowalska', ex2: 'Nowak' },
  { header: 'Prénom', required: true, help: 'Prénom du conducteur.', ex1: 'Zofia', ex2: 'Marek' },
  { header: 'Email', required: false, help: 'Clé de dédoublonnage : une ligne dont l’email existe déjà met à jour le conducteur au lieu d’en créer un second. Laissée vide, une adresse provisoire non routable (pasdemail001@non-renseigne.invalid) est générée et le conducteur est signalé d’une pastille rouge, à renseigner plus tard.', ex1: 'zofia.kowalska@exemple.com', ex2: '' },
  { header: 'Téléphone', required: false, help: 'Format libre.', ex1: '+48 600 000 001', ex2: '06 12 34 56 78' },
  { header: 'N° de permis', required: true, help: 'Deuxième clé de dédoublonnage, avec l’email.', ex1: 'PL-123456789', ex2: 'FR-987654321' },
  { header: 'Date d’expiration du permis', required: true, help: 'JJ/MM/AAAA ou AAAA-MM-JJ (une vraie cellule Date Excel convient aussi).', ex1: '15/06/2029', ex2: '2030-01-31' },
  { header: 'Date de délivrance du permis', required: false, help: 'JJ/MM/AAAA ou AAAA-MM-JJ.', ex1: '15/06/2019', ex2: '2020-01-31' },
  { header: 'Catégories de permis', required: false, help: 'Catégories du permis UE harmonisé (directive 2006/126/CE), séparées par une virgule, parmi AM, A1, A2, A, B1, B, BE, C1, C1E, C, CE, D1, D1E, D, DE. Ex. « B,BE,C1E ». Les libellés longs sont reconnus : « poids lourd » → C, « semi-remorque » / « ensemble routier » → CE, « transport en commun » / « autocar » → D, « remorque » → BE, « cyclomoteur » → AM, « moto » → A. Les en-têtes « Kategoria » (PL) et « Behörighet » (SE) sont acceptés. Une catégorie non reconnue est ignorée et signalée, sans faire échouer la ligne.', ex1: 'B', ex2: 'B,BE,C1E' },
  { header: 'Pays de délivrance', required: false, help: 'Pays où le permis a été délivré : code ISO 3166-1 alpha-2 (FR, PL, SE) ou nom du pays (France, Polska, Sverige). Vide = pays de la filiale qui reçoit l’import.', ex1: 'PL', ex2: 'Sverige' },
  { header: 'Date d’embauche', required: false, help: 'JJ/MM/AAAA ou AAAA-MM-JJ.', ex1: '01/03/2024', ex2: '2023-09-01' },
  { header: 'Date de naissance', required: false, help: 'JJ/MM/AAAA ou AAAA-MM-JJ.', ex1: '12/04/1988', ex2: '1979-11-02' },
  { header: 'Statut', required: false, help: 'Actif, Inactif, Intérimaire, Temporaire, Congé ou Suspendu. Vide = Actif.', ex1: 'Actif', ex2: 'Intérimaire' },
  { header: 'Adresse', required: false, help: 'Adresse postale, format libre.', ex1: '12 rue des Tilleuls, 31000 Toulouse', ex2: 'ul. Kwiatowa 5, Warszawa' },
];

// --- Colonnes véhicules -------------------------------------------------------
// Alignées sur ce que reconnaît le mapping tolérant de VehicleImportModal.
const VEHICLE_COLUMNS = [
  { header: 'Immatriculation', required: true, help: 'Seule colonne obligatoire — elle sert aussi à repérer la ligne d’en-tête.', ex1: 'AB-123-CD', ex2: 'WX-456-YZ' },
  { header: 'Marque', required: false, help: 'Constructeur.', ex1: 'Renault', ex2: 'Skoda' },
  { header: 'Modèle', required: false, help: 'Appellation commerciale.', ex1: 'Kangoo', ex2: 'Octavia' },
  { header: 'Genre', required: false, help: 'Sert à déduire la catégorie : « utilitaire » → VU, « poids lourd » → PL, « engin » → ENGIN, sinon VP.', ex1: 'Utilitaire', ex2: 'Voiture particulière' },
  { header: 'Carrosserie', required: false, help: 'Format libre.', ex1: 'Fourgon', ex2: 'Break' },
  { header: 'Usage', required: false, help: 'Format libre (tournée, direction, chantier…).', ex1: 'Tournée', ex2: 'Direction' },
  { header: 'Énergie', required: false, help: 'Diesel, Essence, Électrique, Hybride, GPL ou GNV.', ex1: 'Diesel', ex2: 'Hybride' },
  { header: 'Date de mise en circulation', required: false, help: 'JJ/MM/AAAA — seule l’année est retenue.', ex1: '01/03/2021', ex2: '2019-06-15' },
  { header: 'Puissance', required: false, help: 'Puissance fiscale ou chevaux, en nombre.', ex1: '95', ex2: '150' },
  { header: 'Places', required: false, help: 'Nombre de places assises.', ex1: '2', ex2: '5' },
  { header: 'Poids', required: false, help: 'PTAC en kilogrammes.', ex1: '2200', ex2: '1800' },
  { header: 'Valeur à neuf', required: false, help: 'Montant en devise de la filiale, sans symbole.', ex1: '24000', ex2: '31500' },
  { header: 'Val. assurée', required: false, help: 'Montant en devise de la filiale, sans symbole.', ex1: '18000', ex2: '27000' },
  { header: 'Mode achat', required: false, help: 'Achat, LLD, LOA, crédit-bail…', ex1: 'LLD', ex2: 'Achat' },
];

const INSTRUCTIONS_HEADER = ['Colonne', 'Obligatoire', 'Format attendu / valeurs acceptées'];

function buildWorkbook({ columns, sheetName, preamble }) {
  const wb = XLSX.utils.book_new();

  // Feuille « Données » : en-têtes + deux lignes d'exemple.
  const data = [
    columns.map(c => c.header),
    columns.map(c => c.ex1 ?? ''),
    columns.map(c => c.ex2 ?? ''),
  ];
  const wsData = XLSX.utils.aoa_to_sheet(data);
  wsData['!cols'] = columns.map(c => ({ wch: Math.max(14, Math.min(38, c.header.length + 6)) }));
  XLSX.utils.book_append_sheet(wb, wsData, sheetName);

  // Feuille « Instructions ».
  const rows = [
    ...preamble.map(line => [line, '', '']),
    ['', '', ''],
    INSTRUCTIONS_HEADER,
    ...columns.map(c => [c.header, c.required ? 'Oui' : 'Non', c.help]),
  ];
  const wsHelp = XLSX.utils.aoa_to_sheet(rows);
  wsHelp['!cols'] = [{ wch: 32 }, { wch: 13 }, { wch: 92 }];
  XLSX.utils.book_append_sheet(wb, wsHelp, 'Instructions');

  return wb;
}

function save(wb, filename) {
  XLSX.writeFile(wb, filename);
}

/** Modèle d'import conducteurs (.xlsx). */
export function downloadDriverTemplate(filename = 'modele_import_conducteurs.xlsx') {
  const wb = buildWorkbook({
    columns: DRIVER_COLUMNS,
    sheetName: 'Conducteurs',
    preamble: [
      'Modèle d’import de conducteurs — FleetManager Max',
      'Renseignez une ligne par conducteur dans la feuille « Conducteurs ».',
      'Les deux lignes d’exemple sont à écraser ou à supprimer avant l’import.',
      'L’ordre des colonnes est libre : elles sont reconnues par leur en-tête (français, anglais, polonais ou suédois).',
      'Les conducteurs sont créés dans la filiale active au moment de l’import.',
      'Un conducteur sans email reçoit une adresse provisoire non routable et une pastille rouge « Email à renseigner » — la ligne n’est jamais rejetée pour autant.',
      'Ré-importer le même fichier met à jour les conducteurs existants (repérés par email ou n° de permis) : aucun doublon.',
    ],
  });
  save(wb, filename);
}

/** Modèle d'import véhicules (.xlsx). */
export function downloadVehicleTemplate(filename = 'modele_import_vehicules.xlsx') {
  const wb = buildWorkbook({
    columns: VEHICLE_COLUMNS,
    sheetName: 'Véhicules',
    preamble: [
      'Modèle d’import de véhicules — FleetManager Max',
      'Renseignez une ligne par véhicule dans la feuille « Véhicules ».',
      'Les deux lignes d’exemple sont à écraser ou à supprimer avant l’import.',
      'L’ordre des colonnes est libre : elles sont reconnues par leur en-tête.',
      'Un export assureur « État de parc » (AXA) s’importe tel quel, sans passer par ce modèle : les garanties, primes et franchises y sont détectées automatiquement.',
      'Les véhicules sont créés dans la filiale active au moment de l’import.',
    ],
  });
  save(wb, filename);
}

export { DRIVER_COLUMNS, VEHICLE_COLUMNS };
